import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { ActionReducer } from '@ngrx/store';
import { defer, from, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { createEffect } from '@ngrx/effects';
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { LoggerService } from "src/service/logger.service";

/*
ATTENTION
This class is built on the source code from the plugin "ngrx-store-ionic-storage". Its purpose is to handle the syncronisation between the ngrx "Store" and the device-specific
local-storage.

LIST OF IMPORTANT MDIFICATIONS:

    -"STORAGE_KEY" value: customized.
    -"Storage" constructor invocation. We pass it an object containing the custom configuration parameter we want.
*/
const STORAGE_KEY = 'APP_STATE';

const storage = new Storage(environment.ionicStoreConfig, {});

// get/setNested inspired by
// https://github.com/mickhansen/dottie.js
function getNested(obj: any, path: string): any {
    if (obj !== null && path) {
        // Recurse into the object.
        const parts = path.split('.').reverse();
        while (obj != null && parts.length) {
            obj = obj[parts.pop()];
        }
    }
    return obj;
}

function setNested(obj: any, path: string, value: any): any {
    if (obj != null && path) {
        let pieces = path.split('.'),
            current = obj,
            piece, i,
            length = pieces.length;

        for (i = 0; i < length; i++) {
            piece = pieces[i];
            if (i === length - 1) {
                current[piece] = value;
            } else if (!current[piece]) {
                current[piece] = {};
            }
            current = current[piece];
        }
    }

    return obj;
}


async function fetchState(): Promise<{}> {
    const fisrtState = await storage.get(STORAGE_KEY);

    if (fisrtState) {
        return fisrtState;
    } else {
        return migrateFromOldStorage();
    }

}

async function migrateFromOldStorage(): Promise<{}> {
    let stateFromOldStorage = {};

    if (await storage.get("local_storage_version")) {

        //#region Migrate old "client_options" key to "AppSettings.KEY_CLIENT_OPTIONS" store feature
        stateFromOldStorage[AppSettings.KEY_CLIENT_OPTIONS] = await storage.get("client_options");
        //#endregion

        //#region Migrate old "plants" key to "AppSettings.KEY_PLANTS" store feature
        stateFromOldStorage[AppSettings.KEY_PLANTS] = await storage.get("plants");
        //#endregion

    }

    return stateFromOldStorage;
}


function saveState(state: any, keys: string[]): Promise<void> {
    // Pull out the portion of the state to save.
    if (keys) {
        state = keys.reduce((acc, k) => {
            const val = getNested(state, k);
            if (val) {
                setNested(acc, k, val);
            }
            return acc;
        }, {})
    }

    return storage.set(STORAGE_KEY, state);
};

export const StorageSyncActions = {
    HYDRATED: 'NSIS_APP_HYDRATED'
}

@Injectable()
export class StorageSyncEffects {

  hydrate$ = createEffect(() =>
    defer(() =>
      from(fetchState()).pipe(
        map(state => ({
          type: StorageSyncActions.HYDRATED,
          payload: state
        })),
        catchError(e => {
          LoggerService.logStatic(
            LogLevel.LOG_ERROR,
            "Ionic-storage-sync-meta-reducer",
            "Ionic-storage-sync-meta-reducer",
            "hydrate$",
            "Error fetching state: " + (e?.message ?? JSON.stringify(e))
          );
          return of({ type: StorageSyncActions.HYDRATED, payload: {} });
        })
      )
    )
  );
}

export interface StorageSyncOptions {
    keys?: string[];
    ignoreActions?: string[];
    hydratedStateKey?: string;
    onSyncError?: (err: any) => void;
};

const defaultOptions: StorageSyncOptions = {
    keys: [],
    ignoreActions: [],
    onSyncError: (err) => { }
}

export function storageSync(options?: StorageSyncOptions) {
    const { keys, ignoreActions, hydratedStateKey, onSyncError } = Object.assign({}, defaultOptions, options || {});

    ignoreActions.push(StorageSyncActions.HYDRATED);
    ignoreActions.push('@ngrx/store/init');
    ignoreActions.push('@ngrx/effects/init');
    ignoreActions.push('@ngrx/store/update-reducers');

    const hydratedState: any = {};

    return function storageSyncReducer(reducer: ActionReducer<any>) {
        return (state: any, action: any) => {
            const { type, payload } = action;

            if (type === StorageSyncActions.HYDRATED) {
                state = Object.assign({}, state, payload);
                if (hydratedStateKey) {
                    hydratedState[hydratedStateKey] = true;
                }
            }

            const nextState = Object.assign({}, reducer(state, action), hydratedState);

            if (ignoreActions.indexOf(type) === -1) {

                saveState(nextState, keys).catch(err => onSyncError(err));
            }

            return nextState;
        }
    }
}